import { useCallback, useEffect, useMemo, useState } from "react";
import { Phone, AlertTriangle, Loader2, Plane } from "lucide-react";
import { toast } from "sonner";
import { ARRIVAL_LABELS, errMsg } from "@/lib/api";
import { useApi, PageHead, ExportMenu } from "@/admin/AdminApp";

const Row = ({ r, onSave }) => {
  const [notes, setNotes] = useState(r.followup_notes || "");
  const digits = r.phone.replace(/[^\d]/g, "");
  return (
    <div className={`rounded-2xl border bg-white p-5 ${r.overdue ? "border-amber-300" : "border-[var(--line)]"}`} data-testid={`arrival-row-${r.person_id}`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg">{r.name || "Unnamed guest"}</span>
            {r.overdue && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800" data-testid="overdue-badge"><AlertTriangle size={12} /> Overdue</span>}
          </div>
          <div className="text-sm text-slate-500">{r.family_name}{r.hotel ? ` · ${r.hotel}` : ""}</div>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-700"><Plane size={14} className="text-[var(--gold-deep)]" />
            {r.arrival_date ? `${r.arrival_date} ${r.arrival_time}` : "No arrival date"} {r.arrival_flight && `· ${r.arrival_flight}`} {r.arrival_airport && `· ${r.arrival_airport}`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className="input-lux !w-auto !py-2" value={r.status} onChange={(e) => onSave(r, { arrival_status: e.target.value, followup_notes: notes })} data-testid={`arrival-status-${r.person_id}`}>
            {Object.entries(ARRIVAL_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {digits && <a href={`tel:+${digits}`} className="btn-ghost !px-3 !py-2" data-testid={`arrival-call-${r.person_id}`}><Phone size={15} /> Call</a>}
        </div>
      </div>
      <textarea className="input-lux mt-3 min-h-[44px] text-sm" placeholder="Follow-up notes" value={notes} onChange={(e) => setNotes(e.target.value)}
        onBlur={() => notes !== (r.followup_notes || "") && onSave(r, { arrival_status: r.status, followup_notes: notes })} data-testid={`arrival-notes-${r.person_id}`} />
    </div>
  );
};

export default function Arrivals() {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [date, setDate] = useState("all");
  const load = useCallback(() => api("get", "/arrivals").then((d) => setRows(d.rows)).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (r, body) => {
    try {
      await api("put", `/arrivals/${r.family_id}/${r.person_id}`, body);
      setRows((rs) => rs.map((x) => (x.person_id === r.person_id ? { ...x, status: body.arrival_status, followup_notes: body.followup_notes, overdue: body.arrival_status === "not_arrived" && x.overdue } : x)));
      toast.success("Updated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dates = useMemo(() => [...new Set((rows || []).map((r) => r.arrival_date).filter(Boolean))].sort(), [rows]);
  if (!rows) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;
  const counts = Object.keys(ARRIVAL_LABELS).reduce((a, k) => ({ ...a, [k]: rows.filter((r) => r.status === k).length }), {});
  const overdue = rows.filter((r) => r.overdue).length;
  const list = rows.filter((r) => (status === "all" || (status === "overdue" ? r.overdue : r.status === status)) && (date === "all" || r.arrival_date === date));

  return (
    <div data-testid="arrivals-page">
      <PageHead title="Arrivals" text="Track every attending guest from landing to hotel. Overdue guests are flagged automatically (India time).">
        <select className="input-lux !w-auto !rounded-full !py-2" value={date} onChange={(e) => setDate(e.target.value)} data-testid="arrival-date-filter">
          <option value="all">All arrival dates</option>{dates.map((d) => <option key={d}>{d}</option>)}
        </select>
        <ExportMenu path="/arrivals/export" name="arrivals" params={{ status, date }} testId="arrivals-export-btn" />
      </PageHead>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {[["all", "All guests", rows.length], ...Object.entries(ARRIVAL_LABELS).map(([k, l]) => [k, l, counts[k]]), ["overdue", "Overdue", overdue]].map(([k, l, v]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`arrival-count-${k}`}
            className={`rounded-2xl border p-4 text-left transition-colors ${status === k ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--ivory)]" : "border-[var(--line)] bg-white"}`}>
            <div className="text-[11px] uppercase tracking-[.18em] opacity-70">{l}</div><div className="font-serif-d mt-1 text-3xl">{v}</div>
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {list.length === 0 && <p className="rounded-2xl bg-white p-10 text-center text-slate-500" data-testid="arrivals-empty">No guests match. Guests appear here once their family is attending.</p>}
        {list.map((r) => <Row key={r.person_id} r={r} onSave={save} />)}
      </div>
    </div>
  );
}
